import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './TutorialDelivery.scss';
import { markTutorialDeliveryAsRead, hasReadTutorialDelivery } from '../../utils/tutorialDeliveryStorage';
import boxImage from '../../assets/tutoriel/box.jpg';
import coupeImage from '../../assets/tutoriel/coupe.png';
import potImage from '../../assets/tutoriel/pot.png';

const TutorialDelivery = () => {
  const [hasRead, setHasRead] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [redirectCountdown, setRedirectCountdown] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    // Vérifier si l'utilisateur a déjà lu le tutoriel
    setHasRead(hasReadTutorialDelivery());
  }, []);

  const handleMarkAsRead = () => {
    markTutorialDeliveryAsRead();
    setHasRead(true);
    setShowConfirmation(true);
    setRedirectCountdown(5);
    
    // Démarrer le compte à rebours
    const countdown = setInterval(() => {
      setRedirectCountdown(prevCount => {
        if (prevCount <= 1) {
          clearInterval(countdown);
          navigate('/products');
          return 0;
        }
        return prevCount - 1;
      });
    }, 1000);
    
    // Masquer la confirmation après 3 secondes mais garder le countdown
    setTimeout(() => {
      setShowConfirmation(false);
    }, 3000);
  };

  return (
    <div className="tutorial-delivery">
      <div className="container">
        <header className="tutorial-header">
          <h1>Tutoriel : Déballage des fleurs</h1>
          <p className="tutorial-subtitle">
            Guide pour préserver au mieux vos fleurs après réception
          </p>
        </header>

        <div className="tutorial-content">
          {/* Section 1: Introduction */}
          <section className="tutorial-section">
            <div className="section-layout">
              <div className="section-text">
                <h2>Réception de vos fleurs</h2>
                <p>
                  Recevoir des fleurs emballées dans des cartons qui ont voyagé entre 24 et 48 heures 
                  implique de respecter une procédure de déballage des colis pour préserver au mieux les fleurs.
                </p>
              </div>
              <div className="section-illustration">
                <div className="illustration-container">
                  <img src={boxImage} alt="Colis de fleurs à déballer" />
                </div>
              </div>
            </div>
          </section>

          {/* Section 2: Coupe des tiges */}
          <section className="tutorial-section">
            <div className="section-layout reverse">
              <div className="section-illustration">
                <div className="illustration-container">
                  <img src={coupeImage} alt="Coupe des tiges en biseau" />
                </div>
              </div>
              <div className="section-text">
                <h2>Coupe des tiges</h2>
                <p>
                  En effet, les fleurs subissent une rupture hydrique et se protègent en asséchant le bord 
                  coupé en bas de la tige, ce qui leur permet de préserver une petite réserve d'humidité 
                  à l'intérieur de la tige : c'est pourquoi il faut <strong>systématiquement recouper de 1 à 2 cm</strong> 
                  tous les bas de tige, car cette partie séchée fait « bouchon » et limite la reprise d'eau.
                </p>
                <div className="tip-box">
                  <i className="fas fa-lightbulb"></i>
                  <span>Dans l'ensemble, les fleuristes privilégient la coupe en biseau, pour augmenter la surface d'absorption.</span>
                </div>
              </div>
            </div>
          </section>

          {/* Section 3: Période d'hydratation */}
          <section className="tutorial-section">
            <div className="section-layout">
              <div className="section-text">
                <h2>Période d'hydratation</h2>
                <p>
                  Tout aussi important, il est absolument nécessaire, durant cette période d'hydratation, 
                  de <strong>laisser les housses ou les papiers d'emballage</strong> autour de chaque botte de fleurs 
                  pendant <strong>au moins 4 heures</strong> (c'est encore mieux toute la nuit).
                </p>
                <p>
                  La raison tient au fait que les têtes des fleurs sont souvent lourdes, ce qui risque de faire 
                  plier la zone déshydratée de la tige sous la fleur : maintenir les fleurs droites pendant 
                  l'hydratation permet à l'eau de bien remonter jusqu'à la fleur (alors que c'est plus difficile 
                  si la tige s'est coudée sous le poids de la fleur).
                </p>
              </div>
              <div className="section-illustration">
                <div className="illustration-container">
                  <img src={potImage} alt="Hydratation des fleurs dans un vase" />
                </div>
              </div>
            </div>
          </section>
        </div>

        {/* Récapitulatif */}
        <div className="tutorial-summary">
          <h3>Points clés à retenir</h3>
          <div className="summary-grid">
            <div className="summary-item">
              <div className="summary-icon">
                <i className="fas fa-cut"></i>
              </div>
              <div className="summary-text">
                <strong>Recouper les tiges</strong>
                <p>1 à 2 cm en biseau</p>
              </div>
            </div>
            <div className="summary-item">
              <div className="summary-icon">
                <i className="fas fa-clock"></i>
              </div>
              <div className="summary-text">
                <strong>Laisser emballé</strong>
                <p>Minimum 4 heures</p>
              </div>
            </div>
            <div className="summary-item">
              <div className="summary-icon">
                <i className="fas fa-tint"></i>
              </div>
              <div className="summary-text">
                <strong>Hydratation</strong>
                <p>Maintenir droites</p>
              </div>
            </div>
          </div>
        </div>

        {/* Bouton Compris! */}
        <div className="tutorial-action">
          {!hasRead ? (
            <button 
              className="tutorial-understood-btn"
              onClick={handleMarkAsRead}
            >
              <i className="fas fa-check-circle"></i>
              Compris !
            </button>
          ) : (
            <div className="tutorial-read-status">
              <i className="fas fa-check-circle"></i>
              Vous avez lu ce tutoriel
            </div>
          )}
          
          {showConfirmation && (
            <div className="tutorial-confirmation">
              <i className="fas fa-check"></i>
              Merci ! Le lien ne sera plus mis en évidence dans la modal de livraison.
            </div>
          )}
          
          {redirectCountdown !== null && redirectCountdown > 0 && (
            <div className="tutorial-redirect">
              <i className="fas fa-clock"></i>
              Redirection vers les produits dans {redirectCountdown} seconde{redirectCountdown > 1 ? 's' : ''}...
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TutorialDelivery;